import { memo, useMemo, useState } from "react";
import { CheckBox, TabBox } from "src/Components";
import { ICCOrderSearchParams } from "src/Interfaces/ccOrder";
import {
  CoveredMatrixOrderTableReport,
  CoveredMatrixExecutionTableSearchParams,
} from "src/Interfaces/coveredMatrixOrder";
import {
  getCoveredMatrixOrdersTable,
  getCoveredMatrixTradesTable,
  getCoveredMatrixErrorsTable,
} from "src/Requests/coveredMatrixOrder";
import TableTemplate from "src/Tables/TableTemplate/tableTemplate";
import { CommonTableUI } from "src/Tables/TableTemplate/common";
import { useErrorTabCount } from "src/Hooks/useErrorTabCount";
import SearchParamBox from "src/Pages/CCOrder/SearchParamBox";
import {
  getMatrixGroupAdditional,
  matrixTradeAdditional,
} from "./GroupActions";
import { tableRefreshInterval } from "../model";
import { RemoteTable } from "./RemoteTable";

export enum MatrixExecutionTab {
  Orders = 1,
  Trades,
  Errors,
}

export const MatrixExecutionTabs = memo(
  ({
    selectedGroupId,
    onSelectGroup,
    onSelectTradeGroup,
    onEditGroup,
    onGroupChanged,
    onGroupsDeleted,
    tableVersion,
  }: {
    selectedGroupId?: number;
    onSelectGroup: (row: CoveredMatrixOrderTableReport) => void;
    onSelectTradeGroup: (row: CoveredMatrixOrderTableReport) => void;
    onEditGroup: (id: number) => void;
    onGroupChanged: (id: number) => void;
    onGroupsDeleted: (ids: number[]) => void;
    tableVersion: number;
  }) => {
    const [tab, setTab] = useState(MatrixExecutionTab.Orders);
    const [tableFilters, setTableFilters] = useState<ICCOrderSearchParams>(
      {} as ICCOrderSearchParams,
    );
    const [showDetails, setShowDetails] = useState(false);
    const searchParam = useMemo<CoveredMatrixExecutionTableSearchParams>(
      () => ({
        accountId: tableFilters.accountId,
        instrumentId: tableFilters.instrumentId,
        profileId: tableFilters.profileId,
      }),
      [
        tableFilters.accountId,
        tableFilters.instrumentId,
        tableFilters.profileId,
      ],
    );
    const {
      errorTabCount,
      errorTableRefreshFlag,
      errorTableAfterFetchHandler,
    } = useErrorTabCount(
      "coveredMatrixOrder",
      tab,
      MatrixExecutionTab.Errors,
      getCoveredMatrixErrorsTable,
      searchParam,
    );
    const tabs = [
      { index: MatrixExecutionTab.Orders, title: "سفارشات" },
      { index: MatrixExecutionTab.Trades, title: "معاملات" },
      { index: MatrixExecutionTab.Errors, title: "خطا", count: errorTabCount },
    ];
    const groupAdditional = useMemo(
      () =>
        getMatrixGroupAdditional(onEditGroup, onGroupChanged, onGroupsDeleted),
      [onEditGroup, onGroupChanged, onGroupsDeleted],
    );
    return (
      <div className="min-w-0 w-full max-w-full overflow-hidden mt-3 pt-5">
        <div className="w-full flex flex-row flex-wrap justify-start items-center gap-3">
          <div className="max-w-min">
            <TabBox
              activeTab={tab}
              children={tabs}
              onChange={(_, index) => setTab(index as MatrixExecutionTab)}
            />
          </div>
          {tab === MatrixExecutionTab.Orders ? (
            <CheckBox
              checked={showDetails}
              label="نمایش جزییات"
              name="showDetails"
              onChange={(_, checked) => setShowDetails(checked)}
            />
          ) : null}
          <SearchParamBox
            searchParam={tableFilters}
            onChange={(name, value) =>
              setTableFilters((current) => ({ ...current, [name]: value }))
            }
          />
        </div>
        <div className="min-w-0 w-full max-w-full overflow-hidden">
          {tab === MatrixExecutionTab.Orders ? (
            <TableTemplate<
              CoveredMatrixExecutionTableSearchParams,
              CoveredMatrixOrderTableReport
            >
              silentRefreshKey={tableVersion}
              searchParameters={searchParam}
              refreshTableTime={tableRefreshInterval}
              showIsLoading={false}
              hideUntilResponse
              withSort
              getDataTable={getCoveredMatrixOrdersTable}
              tableWrapperClassname="!mx-0 !min-w-0 !w-full !max-w-full [&>div]:!min-w-0 [&>div]:!max-w-full"
              TableUI={({ reports, onAction, sortingType, setSortingType }) => (
                <CommonTableUI
                  reports={reports}
                  onAction={onAction}
                  sortingType={sortingType}
                  setSortingType={setSortingType}
                  showHiddenCols={showDetails}
                  AdditionalBody={groupAdditional}
                  withSelect
                  activeRowId={selectedGroupId}
                  onTableRowClick={onSelectGroup}
                  tableBoxClassName="!mx-0 !w-full !max-w-full !overflow-x-auto [&_table]:!min-w-max"
                />
              )}
            />
          ) : null}
          {tab === MatrixExecutionTab.Trades ? (
            <RemoteTable
              key={JSON.stringify(searchParam)}
              title=""
              getData={(offset, pageSize) =>
                getCoveredMatrixTradesTable(searchParam, offset, pageSize)
              }
              onRowClick={onSelectTradeGroup}
              additionalBody={matrixTradeAdditional}
            />
          ) : null}
          {tab === MatrixExecutionTab.Errors ? (
            <RemoteTable
              key={JSON.stringify(searchParam)}
              title=""
              getData={(offset, pageSize) =>
                getCoveredMatrixErrorsTable(searchParam, offset, pageSize)
              }
              onFetchComplete={errorTableAfterFetchHandler}
              refreshFlag={errorTableRefreshFlag}
            />
          ) : null}
        </div>
      </div>
    );
  },
);
